import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Box, Typography, Fab, Popover, Card, Chip, Stack, ToggleButtonGroup, ToggleButton, Badge } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import * as Icons from "../constants/icons";
import { PAGE_FILL_SX, TABLE_FILL_SX } from '../constants/layout';
import { useCrmTableContext } from '../providers/CrmTableProvider';
import { useMasterData } from '../providers/MasterDataProvider';
import StandardDataTable, { Column } from '../components/StandardDataTable';
import CrmSearchFilters from '../components/CrmSearchFilters';
import CrmCustomerForm from '../components/CrmCustomerForm';
import BuildingForm from '../components/BuildingForm';
import BuildingSearchFilters from '../components/BuildingSearchFilters';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';

type CustomerTab = 'all' | 'individual' | 'corporate' | 'building';

export default function CrmCustomerManagement() {
  const { staffList } = useMasterData();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // 一覧の状態は親 Layout (CrmCustomerLayout) の Context から取得。
  // 詳細ページ ↔ 一覧 を行き来してもデータ・page・検索条件が保持される。
  const {
    data,
    totalCount,
    loading,
    page,
    pageSize,
    filters,
    setPage,
    setPageSize,
    updateFilters,
    handleSort,
    refresh,
    scrollPositionRef,
    building,
    isBuildingTab,
    setIsBuildingTab,
  } = useCrmTableContext();

  // タブ状態は Layout に永続される isBuildingTab と filters.customer_type を単一の真実とする。
  // URL ?tab= から派生させると、詳細から戻った際に URL とのズレで同期処理が走り、
  // 不要な再フェッチ → スクロールリセットを誘発するため避ける。
  const currentTab: CustomerTab = isBuildingTab
    ? 'building'
    : (filters.customer_type as CustomerTab) || 'all';

  // 表示中タブのデータソース (顧客一覧 / 建物一覧) を切り替える
  const activeData = isBuildingTab ? building.data : data;
  const activeLoading = isBuildingTab ? building.loading : loading;
  const activeScrollRef = isBuildingTab ? building.scrollPositionRef : scrollPositionRef;

  // 📜 一覧テーブルのスクロール位置を Layout (Context) 経由で復元
  //     - StandardDataTable から TableContainer の ref を受け取り
  //     - マウント時 (= 詳細から戻ってきた時) に保存値へ復元
  //     - スクロール時に Layout の ref を更新
  //     - アンマウント時にも最終値を保存 (バックアップ)
  const tableScrollRef = useRef<HTMLDivElement | null>(null);

  // スクロール位置を「目標値に到達するまで」複数フレームにわたり再適用する。
  // framer-motion の入場アニメーション (AnimatePresence mode="wait") 中は
  // レイアウトが未確定で、行が描画されてもコンテナの高さが確定するまでは
  // scrollTop がスクロール可能上限でクランプされ、1〜2 フレームでは取りこぼす。
  // 目標値で固定できた時点でループを止めるため、ユーザー操作とは競合しない。
  const restoreScroll = useCallback(() => {
    const el = tableScrollRef.current;
    const target = activeScrollRef.current;
    if (!el) return () => {};
    if (target <= 0) {
      // 保存位置なし = 未スクロールのタブ。タブ切替で残った旧タブのスクロールを先頭へ戻す
      el.scrollTop = 0;
      return () => {};
    }

    let rafId = 0;
    let attempts = 0;
    const tick = () => {
      if (el.scrollTop !== target) el.scrollTop = target;
      attempts += 1;
      // まだ目標値に達していなければ (= 高さ未確定でクランプされた) 再試行。
      // 最大 30 フレーム (≒0.5s) で入場アニメーション (0.2s) を十分カバーする。
      if (el.scrollTop !== target && attempts < 30) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [activeScrollRef]);

  // マウント時 (詳細から戻ってきた時) に復元 + スクロール位置を追跡
  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const cancelRestore = restoreScroll();

    const handleScroll = () => {
      activeScrollRef.current = el.scrollTop;
    };
    el.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      cancelRestore();
      // 退避: 0 で上書きしない (StrictMode の二重マウントや DOM detach 経由の 0 取得を防止)
      // タブ切替時はクロージャが旧タブの ref を保持しているため、旧タブ側へ正しく退避される
      if (el.scrollTop > 0) {
        activeScrollRef.current = el.scrollTop;
      }
      el.removeEventListener('scroll', handleScroll);
    };
  }, [restoreScroll, activeScrollRef]);

  // データ取得が完了して行が描画された後にも復元 (初回ロードや再フェッチ後の対応)
  useEffect(() => {
    if (activeLoading) return;
    return restoreScroll();
  }, [activeLoading, activeData, restoreScroll]);

  // filters.customer_type → URL ?tab= の片方向同期 (共有・リロード時の復元用)。
  // state ではなく URL のみ更新するため再フェッチは発生しない。
  useEffect(() => {
    const expected = currentTab === 'all' ? null : currentTab;
    if (searchParams.get('tab') !== expected) {
      setSearchParams(prev => {
        if (expected === null) prev.delete('tab');
        else prev.set('tab', expected);
        return prev;
      }, { replace: true });
    }
  }, [currentTab, searchParams, setSearchParams]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBuildingFormOpen, setIsBuildingFormOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const handleTabChange = (event: React.MouseEvent<HTMLElement>, newValue: CustomerTab | null) => {
    if (newValue === null) return; // 同じボタン再押下による全解除を防ぐ
    if (newValue === 'building') {
      // 建物タブへ切替。顧客側の filters には触れず、顧客一覧の状態をそのまま温存する
      setIsBuildingTab(true);
      return;
    }
    setIsBuildingTab(false);
    // filters を更新すれば currentTab が追従し、URL も上記 effect 経由で同期される。
    // 建物タブから同じ顧客種別へ戻っただけの場合は再フェッチしない
    const newType = newValue === 'all' ? '' : newValue;
    if ((filters.customer_type || '') !== newType) {
      updateFilters({ customer_type: newType });
    }
  };

  const handleOpenSearch = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSearch = () => {
    setAnchorEl(null);
  };

  const openSearch = Boolean(anchorEl);

  // 検索フィルタが有効か (タブで管理する customer_type は除外し、検索ワード等の有無で判定)
  const isFiltered = isBuildingTab
    ? Boolean(
        (building.filters.search_word && building.filters.search_word.length > 0) ||
        building.filters.orphan_only,
      )
    : Boolean(filters.search_word && filters.search_word.length > 0);

  // 顧客一覧の列定義（個人/法人いずれも統一レイアウト）
  const columns = useMemo<Column[]>(() => [
    {
      field: 'customer_name',
      headerName: '氏名 / 会社名',
      width: 300,
      sticky: true,
      renderCell: (row) => {
        const isIndividual = row.customer_type === 'individual';
        const displayName = isIndividual
          ? `${row.last_name || ''} ${row.first_name || ''}`.trim()
          : `${row.company_name || ''} ${row.customer_branch_name || ''}`.trim();
        const furigana = isIndividual
          ? `${row.last_name_kana || ''} ${row.first_name_kana || ''}`.trim()
          : row.company_name_kana || '';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: 1,
                bgcolor: isIndividual ? '#eceff1' : '#e3f2fd',
                color: isIndividual ? blueGrey[600] : '#1976d2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {isIndividual ? <Icons.Person sx={{ fontSize: 16 }} /> : <Icons.Apartment sx={{ fontSize: 16 }} />}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                color: 'text.primary',
                cursor: 'pointer',
                transition: 'color 0.15s',
                '.MuiTableRow-root:hover &': { color: '#1976d2' },
              }}
              onClick={() => {
                // 全画面ページへ遷移。戻り時に一覧のタブ位置 (?tab=...) を維持するため
                // 現在の URL 検索文字列を state.listSearch に乗せて渡す
                navigate(`/crm/customers/${row.id}`, {
                  state: { listSearch: location.search },
                });
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.3,
                  textDecoration: 'none',
                  '.MuiTableRow-root:hover &': { textDecoration: 'underline' },
                }}
              >
                {displayName || row.id}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                {furigana && (
                  <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.3 }} noWrap>
                    {furigana}
                  </Typography>
                )}
                {row.registration_source === 'building_party' && (
                  <Chip
                    label="関係先"
                    size="small"
                    sx={{
                      height: 18,
                      bgcolor: '#ede7f6',
                      color: '#5e35b1',
                      fontWeight: 500,
                      fontSize: '0.65rem',
                      flexShrink: 0,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                )}
                {row.turquoise_torihikisaki_cd && (
                  <Chip
                    icon={<Icons.AccountTree />}
                    label={`TQCD: ${String(row.turquoise_torihikisaki_cd).padStart(8, '0')}`}
                    size="small"
                    sx={{
                      height: 18,
                      bgcolor: '#e9f0f0ff',
                      color: '#8fa0a8ff',
                      fontWeight: 500,
                      fontSize: '0.65rem',
                      fontFamily: 'monospace',
                      flexShrink: 0,
                      '& .MuiChip-icon': {
                        color: '#b0bec5',
                        ml: 0.5,
                        mr: -0.25,
                        fontSize: 12,
                      },
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'contact',
      headerName: '連絡先',
      width: 280,
      renderCell: (row) => {
        const address = row.address_main || '';
        return (
          <Stack spacing={0.3} sx={{ py: 0.5 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Icons.LocationOnOutlined sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
              <Typography variant="body2" noWrap sx={{ color: address ? 'text.primary' : 'text.disabled' }}>
                {address || '-'}
              </Typography>
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Icons.PhoneOutlined sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
              <Typography variant="body2" noWrap sx={{ color: row.phone ? 'text.primary' : 'text.disabled' }}>
                {row.phone || '-'}
              </Typography>
            </Box>
          </Stack>
        );
      }
    },
    {
      field: 'our_staff_name',
      headerName: '担当者',
      width: 180,
      renderCell: (row) => {
        const name: string = row.our_staff_name || '';
        if (!name) return <Typography variant="body2" color="text.disabled">-</Typography>;
        // Phase 2 で row.our_staff_branch_name が入ったらそちらを優先、
        // 現時点は staffList から拠点名を引く（担当者退職時は空表示）
        const branchName: string = row.our_staff_branch_name
          || staffList.find((s: any) => s.staff_id === row.our_staff_id)?.branch_name
          || '';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, py: 0.5 }}>
            {branchName && (
              <Chip
                label={branchName}
                size="small"
                sx={{
                  bgcolor: '#f5f5f5',
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  height: 22,
                  borderRadius: '4px',
                }}
              />
            )}
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Icons.PersonOutline sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" noWrap>{name}</Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'link_summary',
      headerName: '紐付け',
      width: 120,
      renderCell: (row) => {
        const projectCount = Number(row.project_count || 0);
        const buildingCount = Number(row.building_count || 0);
        return (
          <Stack spacing={0.3} sx={{ py: 0.5 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Icons.FolderOpen sx={{ fontSize: 14, color: projectCount > 0 ? '#1976d2' : 'text.disabled', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: projectCount > 0 ? 'text.primary' : 'text.disabled', lineHeight: 1.3 }}>
                案件 {projectCount}
              </Typography>
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Icons.HomeWork sx={{ fontSize: 14, color: buildingCount > 0 ? '#2e7d32' : 'text.disabled', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: buildingCount > 0 ? 'text.primary' : 'text.disabled', lineHeight: 1.3 }}>
                建物 {buildingCount}
              </Typography>
            </Box>
          </Stack>
        );
      }
    },
    {
      field: 'updated_at',
      headerName: '最終更新',
      width: 140,
      renderCell: (row) => {
        const updated: string = row.updated_at || '';
        const date = updated.substring(0, 10);
        const time = updated.substring(11, 16);
        return (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.3 }}>{date || '-'}</Typography>
            {time && (
              <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.3 }}>{time}</Typography>
            )}
          </Stack>
        );
      }
    },
    // location.search を依存に含めないと onClick 内の値が初回値で固定され、
    // タブ切替後に詳細→戻る で常に "全て" タブへ復帰してしまう
  ], [staffList, location.search, navigate]);

  // 建物住所タブの列定義
  const buildingColumns = useMemo<Column[]>(() => [
    {
      field: 'building_name',
      headerName: '建物名称',
      width: 300,
      sticky: true,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: 1,
              bgcolor: '#e8f5e9',
              color: '#2e7d32',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icons.HomeWork sx={{ fontSize: 16 }} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              color: 'text.primary',
              cursor: 'pointer',
              transition: 'color 0.15s',
              '.MuiTableRow-root:hover &': { color: '#1976d2' },
            }}
            onClick={() => {
              navigate(`/crm/customers/building/${row.id}`, {
                state: { listSearch: location.search },
              });
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.3,
                  '.MuiTableRow-root:hover &': { textDecoration: 'underline' },
                }}
              >
                {row.building_name || row.id}
              </Typography>
              {row.registration_source === 'customer_auto' && (
                <Chip
                  label="簡易"
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fff3e0', color: '#e65100', flexShrink: 0 }}
                />
              )}
            </Box>
            {row.building_name_kana && (
              <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.3 }} noWrap>
                {row.building_name_kana}
              </Typography>
            )}
          </Box>
        </Box>
      )
    },
    {
      field: 'address_main',
      headerName: '住所',
      width: 280,
      renderCell: (row) => (
        <Stack spacing={0.3} sx={{ py: 0.5 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Icons.LocationOnOutlined sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
            <Typography variant="body2" noWrap sx={{ color: row.address_main ? 'text.primary' : 'text.disabled' }}>
              {row.address_main || '-'}
            </Typography>
          </Box>
          {row.postal_code && (
            <Typography variant="caption" color="text.disabled" sx={{ pl: 2.4, lineHeight: 1.3 }}>
              〒{row.postal_code}
            </Typography>
          )}
        </Stack>
      )
    },
    {
      field: 'building_type',
      headerName: '種別',
      width: 140,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, py: 0.5 }}>
          {row.building_type ? (
            <Chip
              label={row.building_type}
              size="small"
              sx={{ bgcolor: '#f5f5f5', color: 'text.secondary', fontSize: '0.8rem', height: 22, borderRadius: '4px' }}
            />
          ) : (
            <Typography variant="body2" color="text.disabled">-</Typography>
          )}
          {row.structure && (
            <Typography variant="caption" color="text.disabled" noWrap sx={{ lineHeight: 1.3 }}>
              {row.structure}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'owner_names',
      headerName: '所有者',
      width: 220,
      renderCell: (row) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, py: 0.5 }}>
          <Icons.RealEstateAgent sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
          <Typography variant="body2" noWrap sx={{ color: row.owner_names ? 'text.primary' : 'text.disabled' }}>
            {row.owner_names || '-'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'updated_at',
      headerName: '最終更新',
      width: 140,
      renderCell: (row) => {
        const updated: string = row.updated_at || '';
        const date = updated.substring(0, 10);
        const time = updated.substring(11, 16);
        return (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.3 }}>{date || '-'}</Typography>
            {time && (
              <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.3 }}>{time}</Typography>
            )}
          </Stack>
        );
      }
    },
  ], [location.search, navigate]);

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 3, width: '95%', margin: '0 auto' }}>
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: blueGrey[500], display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Icons.People /> 顧客管理システム
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <ToggleButtonGroup
          value={currentTab}
          exclusive
          onChange={handleTabChange}
          sx={{
            bgcolor: '#f5f5f7',
            borderRadius: 2,
            p: 0.3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            gap: 0.5,
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: '6px !important',
              px: 1.5,
              py: 0.4,
              textTransform: 'none',
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.8125rem',
              gap: 0.75,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
              '&.Mui-selected': {
                bgcolor: '#607d8b',
                color: '#ffffff',
              },
            },
          }}
        >
          <ToggleButton value="all">
            <Icons.List sx={{ fontSize: 16 }} />全て
          </ToggleButton>
          <ToggleButton value="individual">
            <Icons.Person sx={{ fontSize: 16 }} />個人顧客
          </ToggleButton>
          <ToggleButton value="corporate">
            <Icons.Business sx={{ fontSize: 16 }} />法人顧客
          </ToggleButton>
          <ToggleButton value="building">
            <Icons.HomeWork sx={{ fontSize: 16 }} />建物住所
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* 検索ボタン (検索条件が有効な時は Badge とアイコン塗り潰しで明示) */}
          <Badge
            color="warning"
            variant="dot"
            invisible={!isFiltered}
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ '& .MuiBadge-dot': { boxShadow: '0 0 0 2px white' } }}
          >
            <Fab variant="extended" onClick={handleOpenSearch} sx={STANDARD_FAB_CTA_SX}>
              {isFiltered ? (
                <Icons.FilterAlt sx={{ color: 'warning.main' }} />
              ) : (
                <Icons.FilterAltOutlined />
              )}
              {isBuildingTab ? '建物検索' : '顧客検索'}
            </Fab>
          </Badge>

          {/* 新規登録ボタン */}
          <Fab
            variant="extended"
            onClick={() => {
              if (isBuildingTab) {
                setIsBuildingFormOpen(true);
              } else {
                setSelectedCustomerId(null);
                setIsFormOpen(true);
              }
            }}
            sx={STANDARD_FAB_CTA_SX}
          >
            <Icons.Add />
            {isBuildingTab ? '新規建物登録' : '新規顧客登録'}
          </Fab>
        </Box>
      </Box>

      <Popover
        open={openSearch}
        anchorEl={anchorEl}
        onClose={handleCloseSearch}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, width: 300, borderRadius: 2, boxShadow: 3 } }}
      >
        <Card sx={{ border: 'none', boxShadow: 'none' }}>
          <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', bgcolor: '#f1f5f9' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>検索条件</Typography>
          </Box>
          {isBuildingTab ? (
            <BuildingSearchFilters
              filters={building.filters}
              onSearch={(newFilters) => {
                building.updateFilters(newFilters);
                handleCloseSearch();
              }}
            />
          ) : (
            <CrmSearchFilters
              filters={filters}
              fixedCustomerType={currentTab === 'all' || currentTab === 'building' ? '' : currentTab}
              onSearch={(newFilters) => {
                updateFilters(newFilters);
                handleCloseSearch();
              }}
            />
          )}
        </Card>
      </Popover>

      <Box sx={TABLE_FILL_SX}>
        <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <StandardDataTable
            rows={activeData}
            loading={activeLoading}
            loadingMessage={isBuildingTab ? '建物データを取得中' : '顧客データを取得中'}
            totalCount={isBuildingTab ? building.totalCount : totalCount}
            page={isBuildingTab ? building.page : page}
            pageSize={isBuildingTab ? building.pageSize : pageSize}
            columns={isBuildingTab ? buildingColumns : columns}
            onPageChange={isBuildingTab ? building.setPage : setPage}
            onPageSizeChange={isBuildingTab ? building.setPageSize : setPageSize}
            onSortChange={isBuildingTab ? building.handleSort : handleSort}
            scrollContainerRef={tableScrollRef}
          />
        </Box>
      </Box>

      {isFormOpen && (
        <CrmCustomerForm
          customerId={selectedCustomerId}
          open={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSuccess={(savedId) => {
            setIsFormOpen(false);
            refresh();
            navigate(`/crm/customers/${savedId}`);
          }}
        />
      )}

      {isBuildingFormOpen && (
        <BuildingForm
          buildingId={null}
          open={isBuildingFormOpen}
          onClose={() => setIsBuildingFormOpen(false)}
          onSuccess={(savedId) => {
            setIsBuildingFormOpen(false);
            building.refresh();
            navigate(`/crm/customers/building/${savedId}`);
          }}
        />
      )}
    </Box>
  );
}
